import React from 'react';
import { createRoot } from 'react-dom/client';
import { CONFIG_DEFAULTS } from './constants';
import { App } from './App';
import merge from 'lodash.merge';
import { Store } from './store';
import { FullPartial } from './utils/types';
import { addRemarkEventer } from '@outdoorly/chat-sdk';
import type { Overrides } from './theme';

export type ChatConfig = {
  // goes directly into the chakra theme
  // pull typing from overrides, other keys set won't have any effect
  colors?: Overrides['colors'];
  font?: Overrides['fonts'];

  labels: typeof CONFIG_DEFAULTS.labels;

  // TODO maybe this should be exposed via the sdk into `shouldAppear`?
  show: boolean;

  // not being used for anything currently
  // position?: any;

  // probably not something we need to let them override right away - Nick
  // sounds?: any;
};

type BirdieWindow = {
  configuration: ChatConfig;
  updateConfiguration: (configUpdateObj: FullPartial<ChatConfig>) => BirdieWindow;
};

export type ChatConfigWindow = Window &
  typeof globalThis & {
    Birdie: BirdieWindow;
    Shopify: any;
  };
declare let window: ChatConfigWindow;

const initConfig: FullPartial<ChatConfig> =
  (window.Birdie as BirdieWindow & { init?: { config?: FullPartial<ChatConfig> } })?.init?.config ?? {};
window.Birdie = {
  updateConfiguration(this: BirdieWindow, configUpdateObj: FullPartial<ChatConfig>): BirdieWindow {
    Store.update((s) => {
      s.config = merge(s.config, configUpdateObj, {
        // ensure NPSResponseLabels cannot be overridden,  TODO: mirror in typing
        labels: { NPSResponseLabels: CONFIG_DEFAULTS.labels.NPSResponseLabels }
      });
    });
    return this;
  },
  get configuration(): ChatConfig {
    return Store.getRawState().config;
  }
};
window.Birdie.updateConfiguration(initConfig);

function addPolyfills() {
  const scriptElement = document.createElement('script');
  scriptElement.src =
    'https://polyfill.io/v3/polyfill.min.js?features=AbortController,Array.from,Array.prototype.at,Array.prototype.copyWithin,Array.prototype.entries,Array.prototype.fill,Array.prototype.find,Array.prototype.findIndex,Array.prototype.flatMap,Array.prototype.includes,Array.prototype.keys,Array.prototype.sort,Array.prototype.values,ArrayBuffer,ArrayBuffer.isView,atob,Blob,console,DataView,document,Element,es5,fetch,Float32Array,getComputedStyle,globalThis,innerHeight,innerWidth,Int8Array,IntersectionObserver,Intl,JSON,localStorage,Map,matchMedia,Math.clz32,Math.cosh,Math.sinh,Math.trunc,modernizr:es6string,MutationObserver,Number.isInteger,Number.isNaN,Object.assign,Object.entries,Object.fromEntries,Object.getOwnPropertyDescriptors,Object.getOwnPropertySymbols,Object.is,Object.isExtensible,Object.isFrozen,Object.preventExtensions,Object.setPrototypeOf,Object.values,Promise,Promise.prototype.finally,Reflect,Reflect.apply,Reflect.construct,Reflect.defineProperty,Reflect.deleteProperty,Reflect.get,Reflect.getOwnPropertyDescriptor,Reflect.has,Reflect.ownKeys,Reflect.set,RegExp.prototype.flags,requestAnimationFrame,ResizeObserver,Set,String.prototype.codePointAt,Symbol,Symbol.asyncIterator,Symbol.for,Symbol.iterator,Symbol.keyFor,Symbol.prototype.description,Symbol.replace,Symbol.toStringTag,TextDecoder,TextEncoder,Uint8Array,Uint8ClampedArray,URL,URLSearchParams,WeakMap,WeakSet';
  document.head.appendChild(scriptElement);
}

function createContainer(id: string): HTMLElement {
  const container = document.createElement('div');
  container.setAttribute('id', id);

  document.body.append(container);
  return container;
}
function getContainerElement(id: string): HTMLElement {
  return document.getElementById(id) ?? createContainer(id);
}
addPolyfills();
addRemarkEventer();
const root = createRoot(getContainerElement('remark-chat-app'));
root.render(<App />);
